import React, { useMemo, useState } from 'react';
import { Button, Space, Input, Form, Select, Row, Col, Tag, Typography, notification } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { TableCustom } from '../../../components/TableCustom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getListBlocksRequest } from '../../../services/onlinestore/blockServiceRequest';
import { Link, useNavigate } from 'react-router-dom';
import { newObjectKeys } from '../../../utils/utility';
import { getListMiniAppsRequest } from '../../../services/onlinestore/miniAppsServiceRequest';
import { deleteBlockItemRequest, getListBlockItemsRequest } from '../../../services/onlinestore/blockItemServiceRequest';
import { AppSelect } from '../../../constants/appSelect';

const { Text } = Typography;

const QuanLyItemKhoi = () => {
  const navigate = useNavigate();
  const [formSearch] = Form.useForm();
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
  });

  const {
    data: dataBlockItems,
    isPending,
    refetch: refetchDataBlockItems,
  } = useQuery({ queryKey: ['dataBlockItems', params], queryFn: () => getListBlockItemsRequest(params) });

  // get data blocks
  const { data: dataBlocks } = useQuery({
    queryKey: ['dataBlocks'],
    queryFn: () => getListBlocksRequest({ page: 1, limit: 1000 }),
  });

  // get data miniapps
  const { data: dataMiniApps } = useQuery({
    queryKey: ['dataMiniApps'],
    queryFn: () => getListMiniAppsRequest({ page: 1, limit: 1000 }),
  });

  // convert list blocks options
  const dataBlocksOption = useMemo(() => {
    const options =
      dataBlocks?.data?.map((item) => {
        return { label: item?.title, value: item?.id };
      }) || [];
    return [{ label: 'Tất cả', value: -1 }, ...options];
  }, [dataBlocks]);

  // convert list miniapps options
  const dataMiniAppsOption = useMemo(() => {
    const options =
      dataMiniApps?.data?.map((item) => {
        return { label: item?.title, value: item?.id };
      }) || [];
    return [{ label: 'Tất cả', value: -1 }, ...options];
  }, [dataMiniApps]);

  const { mutate: mutateDeleteBlockItem } = useMutation({
    mutationFn: deleteBlockItemRequest,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({
          message: 'Xóa item khối thành công!',
        });
        refetchDataBlockItems();
      } else {
        notification.error({
          message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const handleSubmitForm = (values) => {
    const temp = { page: 1, limit: params.limit };
    if (values?.title) {
      Object.assign(temp, { title: values?.title });
    }

    if (values?.block_id >= 0) {
      Object.assign(temp, { block_id: [values?.block_id] });
    }

    if (values?.miniapp_id >= 0) {
      Object.assign(temp, { miniapp_id: [values?.miniapp_id] });
    }

    if (values?.status >= 0) {
      Object.assign(temp, { status: [values?.status] });
    }

    setParams(temp);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Tên item khối',
      dataIndex: ['id', 'title'],
      key: 'id',
      render: (_, { title, id }) => (
        <Link to={`/online-store/quan-ly-item-khoi/${id}/sua-item-khoi`} className="text-[#1677FF]">
          {title}
        </Link>
      ),
    },
    {
      title: 'Tên khối',
      dataIndex: 'block_name',
      key: 'block_id',
      render: (block_name) => <Text className="text-[#1677FF]">{block_name}</Text>,
    },
    {
      title: 'Icon',
      dataIndex: 'icon',
      key: 'icon',
      render: (icon) => (icon ? <img src={icon} alt="" className="w-10 h-auto object-contain" /> : ''),
    },
    {
      title: 'Link url',
      dataIndex: 'url',
      key: 'url',
      render: (url) => <Text className="text-[#667085]">{url}</Text>,
    },
    {
      title: 'Miniapp',
      dataIndex: 'miniapp_name',
      key: 'minapp_id',
      render: (miniapp_name) => <Text className="text-[#1677FF]">{miniapp_name}</Text>,
    },
    {
      title: 'Thứ tự',
      dataIndex: 'order_place',
      key: 'order_place',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag className="px-2 py-1" color={AppSelect.STATUS?.find((item) => item?.value === status)?.color}>
          {AppSelect.STATUS?.find((item) => item?.value === status)?.label}
        </Tag>
      ),
    },
    {
      title: 'Cập nhật lần cuối',
      dataIndex: ['updated_by', 'updated_at'],
      key: 'updated_at',
      render: (_, { updated_by, updated_at }) => (
        <Space direction="vertical">
          {updated_by && <Text className="text-[#667085]">{updated_by}</Text>}
          {updated_at && <Text className="text-[#667085]">{updated_at}</Text>}
        </Space>
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Space size={0}>
          <Button type="text" onClick={() => navigate(`/online-store/quan-ly-item-khoi/${record?.id}/sua-item-khoi`)}>
            <EditOutlined />
          </Button>
          <Button type="text" onClick={() => mutateDeleteBlockItem({ id: record?.id })}>
            <DeleteOutlined />
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="m-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl">Danh sách item khối</h1>
        <Link to={'/online-store/quan-ly-item-khoi/them-item-khoi'}>
          <Button type="primary">
            <PlusOutlined />
            Thêm item khối
          </Button>
        </Link>
      </div>

      <Form
        name="search_blocks"
        form={formSearch}
        layout="vertical"
        requiredMark={false}
        className="mb-5"
        onFinish={handleSubmitForm}
      >
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="title" label={<Text>Tên item khối</Text>}>
              <Input placeholder="Nhập tên item khối" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="block_id" label={<Text>Khối</Text>} initialValue={-1}>
              <Select
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={dataBlocksOption}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="miniapp_id" label={<Text>Miniapp</Text>} initialValue={-1}>
              <Select
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={dataMiniAppsOption}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="status" label={<Text>Trạng thái</Text>} initialValue={-1}>
              <Select
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={[
                  { value: -1, label: 'Tất cả' },
                  { value: 0, label: 'Chưa duyệt' },
                  { value: 1, label: 'Đã duyệt' },
                ]}
              />
            </Form.Item>
          </Col>
          <Col className="flex items-center gap-2">
            <Button type="primary" htmlType="submit">
              <SearchOutlined />
              Tìm kiếm
            </Button>
            {params &&
              Object.values(newObjectKeys(params, ['title', 'block_id', 'miniapp_id', 'status'])).some(
                (value) => value
              ) && (
                <Button
                  htmlType="button"
                  onClick={() => {
                    formSearch.resetFields();
                    setParams({ page: 1, limit: params.limit });
                  }}
                >
                  <DeleteOutlined />
                  Xóa lọc
                </Button>
              )}
          </Col>
        </Row>
      </Form>

      <TableCustom
        columns={columns}
        dataSource={dataBlockItems?.data}
        totalItem={dataBlockItems?.total}
        params={params}
        setParams={setParams}
      />
    </div>
  );
};

export default QuanLyItemKhoi;
